import React from "react";
import { Loader2, Trash2, UserPlus, ShieldCheck, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, authHeaders, apiError } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuth";

const field =
  "w-full rounded-lg border border-white/12 bg-[#0B0F1A] px-3.5 py-2.5 text-sm text-white outline-none transition-colors duration-200 placeholder:text-[#5D677F] focus:border-[#8E9AEA]";
const label = "text-[11px] font-bold uppercase tracking-[0.18em] text-[#A6B0C9]";

export const UsersPanel = () => {
  const { user } = useAdminAuth();
  const [users, setUsers] = React.useState([]);
  const [form, setForm] = React.useState({ name: "", email: "", password: "", role: "author" });
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users", { headers: authHeaders() });
      setUsers(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form, { headers: authHeaders() });
      toast.success(`${form.email} can now sign in`);
      setForm({ name: "", email: "", password: "", role: "author" });
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove access for ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`, { headers: authHeaders() });
      toast.success("Access removed");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="mt-8" data-testid="admin-users-panel">
      <h2 className="font-display text-lg font-bold text-white">Team access ({users.length})</h2>
      <p className="mt-1 text-xs text-[#A6B0C9]">
        Authors can create, edit and delete blog articles. Admins can do that plus manage resource files, leads and
        team access.
      </p>

      <form
        onSubmit={submit}
        className="mt-6 grid gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:grid-cols-2 sm:p-6"
        data-testid="admin-user-form"
      >
        <div>
          <p className={label}>Name</p>
          <input value={form.name} onChange={set("name")} className={`${field} mt-2`} placeholder="Priya Sharma" data-testid="admin-user-name" />
        </div>
        <div>
          <p className={label}>Work email</p>
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            required
            className={`${field} mt-2`}
            placeholder="priya@lanvaus.com"
            data-testid="admin-user-email-input"
          />
        </div>
        <div>
          <p className={label}>Temporary password (min 10 characters)</p>
          <input
            type="text"
            value={form.password}
            onChange={set("password")}
            required
            minLength={10}
            className={`${field} mt-2`}
            placeholder="Share this over a secure channel"
            data-testid="admin-user-password"
          />
        </div>
        <div>
          <p className={label}>Role</p>
          <select value={form.role} onChange={set("role")} className={`${field} mt-2`} data-testid="admin-user-role">
            <option value="author">Author — blogs only</option>
            <option value="admin">Admin — full access</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy} className="btn-primary !px-5 !py-2.5 !text-sm" data-testid="admin-user-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Create account
          </button>
        </div>
      </form>

      <div className="mt-8 flex flex-col gap-3" data-testid="admin-users-list">
        {users.map((u) => (
          <div
            key={u.id}
            className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`admin-user-row-${u.email}`}
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${
                    u.role === "admin" ? "bg-[#8E9AEA]/15 text-[#8E9AEA]" : "bg-white/10 text-[#DDE3F2]"
                  }`}
                >
                  {u.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <PenLine className="h-3 w-3" />}
                  {u.role}
                </span>
                {u.email === user?.email && (
                  <span className="text-[10px] uppercase tracking-[0.14em] text-[#7BE0B0]">You</span>
                )}
              </div>
              <p className="mt-1.5 truncate font-semibold text-white">{u.name}</p>
              <p className="truncate text-xs text-[#5D677F]">{u.email}</p>
            </div>
            {u.email !== user?.email && (
              <button
                onClick={() => remove(u)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#F2707A]/30 px-3 py-2 text-xs font-semibold text-[#F2707A] transition-colors duration-200 hover:bg-[#F2707A]/10"
                data-testid={`admin-user-delete-${u.email}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
